import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Flex, Spinner, Text } from "@chakra-ui/react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { FiFileText } from "react-icons/fi";
import { AppBar, APP } from "../../components/mobileUi";
import {
  DesktopBack,
  DesktopPage,
  DesktopPanel,
  useIsDesktop,
} from "../../components/desktopUi";
import {
  ActionPill,
  CardBox,
  CardHeaderIcon,
  ClampDescription,
} from "./trainingUi";
import { getTrainingLecturesApi } from "../../api/training.api";
import { EMP_BASE_PATH } from "../../config";

const LIMIT = 10;
const TEXT = "#18212F";
const MUTED = "#94A3B8";

const LectureCard = ({ item, onClick }) => (
  <CardBox onClick={onClick}>
    <Flex align="center" gap="8px">
      <CardHeaderIcon icon={<FiFileText />} />
      <Text fontSize="14px" fontWeight={600} color={TEXT} noOfLines={1} flex="1">
        {item.title}
      </Text>
    </Flex>
    <Box h="10px" />
    <ClampDescription html={item.description} />
    <Box h="12px" />
    <Flex justify="flex-end">
      <ActionPill />
    </Flex>
  </CardBox>
);

const LectureList = () => {
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();
  const { topicId } = useParams();
  const location = useLocation();
  const topicName = location.state?.topicName || "";

  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [loading, setLoading] = useState(false);
  const reqId = useRef(0);

  const fetchPage = useCallback(
    async (p, mode) => {
      if (!topicId) return;
      reqId.current += 1;
      const rid = reqId.current;
      setLoading(true);
      try {
        const data = await getTrainingLecturesApi(topicId, {
          page: p,
          limit: LIMIT,
        });
        if (rid !== reqId.current) return;
        const list = data?.items || [];
        setItems((prev) => (mode === "append" ? [...prev, ...list] : list));
        setHasNext(!!data?.pagination?.hasNextPage);
        setPage(p);
      } catch {
        if (rid === reqId.current && mode !== "append") setItems([]);
      } finally {
        if (rid === reqId.current) setLoading(false);
      }
    },
    [topicId]
  );

  useEffect(() => {
    fetchPage(1, "replace");
  }, [fetchPage]);

  const pageBody = (
    <>

      <Box px="16px" pt="12px">
        {loading && !items.length ? (
          <Flex justify="center" py="40px">
            <Spinner color={APP.primary200} />
          </Flex>
        ) : items.length === 0 ? (
          <Text fontSize="14px" color={MUTED} textAlign="center" pt="40px">
            Không có bài giảng
          </Text>
        ) : (
          <Flex direction="column" gap="14px">
            {items.map((it) => (
              <LectureCard
                key={it._id}
                item={it}
                onClick={() =>
                  navigate(`${EMP_BASE_PATH}/dao-tao/bai-giang/${it._id}`, {
                    state: { topicName, courseName: it.title },
                  })
                }
              />
            ))}
            {hasNext && (
              <Flex
                as="button"
                onClick={() => fetchPage(page + 1, "append")}
                justify="center"
                py="12px"
                color={APP.primary200}
                fontWeight={600}
                fontSize="14px"
              >
                {loading ? <Spinner size="sm" /> : "Xem thêm"}
              </Flex>
            )}
          </Flex>
        )}
      </Box>
    </>
  );

  // ===================== Bản WEB (desktop) =====================
  if (isDesktop) {
    return (
      <DesktopPage title={topicName || "Bài giảng"} right={<DesktopBack onClick={() => navigate(-1)} />}>
        <DesktopPanel p="0" pb="24px">
          {pageBody}
        </DesktopPanel>
      </DesktopPage>
    );
  }

  return (
    <Box maxW="480px" mx="auto" pb="40px">
      <AppBar title={topicName || "Bài giảng"} />
      {pageBody}
    </Box>
  );
};

export default LectureList;
